/* ═══════════════════════════════════════════════════════════════════════════
   MAGHIL ANALYTICS — VOICE AGENT REPORT — MOCK DATA + DERIVATIONS
   ---------------------------------------------------------------------------
   Front-end-only sample data layer for the Voice Agent Report (VA-1 … VA-10).
   A deterministic pool of sample calls is generated once, then every KPI,
   chart, table and drill-in is DERIVED from it for the selected date range.
   No backend is required — this powers the live preview.
   ═══════════════════════════════════════════════════════════════════════════ */

import { getCurrencySymbol } from '../../utils/currency';
import { resolveTokenRange } from '../../utils/reportPeriod';

/* ─── Types ─────────────────────────────────────────────────────────────── */

export type CallOutcome =
    | "order_completed"
    | "transferred"
    | "abandoned"
    | "inquiry_only"
    | "hangup";

export type TransferReason =
    | "customer_requested"
    | "agent_couldnt_understand"
    | "out_of_scope"
    | "repeated_failures";

export type EightySixAction =
    | "accepted_alternative"
    | "removed_item"
    | "abandoned"
    | "transferred";

export interface OrderItem {
    name: string;
    qty: number;
    price: number;
    modifiers?: string[];
}

export interface EightySixEncounter {
    itemName: string;
    price: number;
    customerAction: EightySixAction;
    alternativeOffered?: string;
}

export interface TranscriptLine {
    speaker: "agent" | "caller";
    text: string;
    t: string; // mm:ss offset
}

export interface VACall {
    callId: string;
    startTime: string; // ISO
    date: string; // YYYY-MM-DD
    hour: number; // 0–23
    durationSeconds: number;
    outcome: CallOutcome;
    orderingIntent: boolean;
    orderStarted: boolean;
    orderConfirmed: boolean;
    orderSubmitted: boolean;
    orderId?: string;
    orderTotal?: number;
    orderItems?: OrderItem[];
    orderStatus?: "Confirmed" | "Picked Up" | "Cancelled";
    transferReason?: TransferReason;
    eightySixed: EightySixEncounter[];
    qualityOverall?: number;
    qualityAccuracy?: number;
    qualityTone?: number;
    qualityEfficiency?: number;
    isRepeatCaller: boolean;
    callerHash: string;
    customerName: string;
    transcript: TranscriptLine[];
}

/* ─── Deterministic PRNG (mulberry32) ───────────────────────────────────── */

export function mulberry32(seed: number) {
    let a = seed >>> 0;
    return function () {
        a |= 0;
        a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/* ─── Menu + reference pools ────────────────────────────────────────────── */

const MENU: { name: string; price: number }[] = [
    { name: "Chicken Biryani", price: 14.99 },
    { name: "Butter Chicken", price: 13.49 },
    { name: "Garlic Naan", price: 3.49 },
    { name: "Mango Lassi", price: 5.99 },
    { name: "Samosa (2 pc)", price: 4.99 },
    { name: "Paneer Tikka", price: 11.99 },
    { name: "Lamb Vindaloo", price: 16.49 },
    { name: "Veg Spring Rolls", price: 6.49 },
    { name: "Tandoori Chicken", price: 15.99 },
    { name: "Gulab Jamun", price: 4.49 },
    { name: "Chicken 65", price: 12.49 },
    { name: "Masala Dosa", price: 10.99 },
];

const EIGHTY_SIX_POOL = ["Mango Lassi", "Gulab Jamun", "Lamb Vindaloo", "Paneer Tikka"];
const ALTERNATIVES: Record<string, string> = {
    "Mango Lassi": "Sweet Lassi",
    "Gulab Jamun": "Rasmalai",
    "Lamb Vindaloo": "Lamb Rogan Josh",
    "Paneer Tikka": "Paneer Tikka Masala",
};

const TRANSFER_REASONS: TransferReason[] = [
    "customer_requested",
    "out_of_scope",
    "agent_couldnt_understand",
    "repeated_failures",
];

const MODIFIERS = ["Extra Spicy", "Mild", "No Onion", "Extra Sauce", "Gluten Free"];

/* ─── Helpers ───────────────────────────────────────────────────────────── */

const pad = (n: number) => String(n).padStart(2, "0");
export const toDateStr = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

function pick<T>(rnd: () => number, arr: T[]): T {
    return arr[Math.floor(rnd() * arr.length)];
}

// Hour weighting — lunch + dinner rush. Index = hour (6 AM … 11 PM operating window).
const HOUR_WEIGHTS: Record<number, number> = {
    6: 0.3, 7: 0.6, 8: 0.9, 9: 1.1, 10: 1.6, 11: 3.2, 12: 5.0, 13: 3.8,
    14: 1.8, 15: 1.0, 16: 1.4, 17: 3.0, 18: 4.6, 19: 4.2, 20: 3.0, 21: 1.6, 22: 0.7, 23: 0.3,
};
const OPERATING_HOURS = Object.keys(HOUR_WEIGHTS).map(Number);

function weightedHour(rnd: () => number): number {
    const total = OPERATING_HOURS.reduce((s, h) => s + HOUR_WEIGHTS[h], 0);
    let r = rnd() * total;
    for (const h of OPERATING_HOURS) {
        r -= HOUR_WEIGHTS[h];
        if (r <= 0) return h;
    }
    return 12;
}

/* ─── Transcript builders ───────────────────────────────────────────────── */

function buildTranscript(outcome: CallOutcome, items: OrderItem[], rnd: () => number): TranscriptLine[] {
    const L: TranscriptLine[] = [];
    L.push({ speaker: "agent", text: "Thank you for calling Spice Garden! This is your AI assistant. How can I help you today?", t: "0:02" });
    if (outcome === "hangup") {
        return L;
    }
    if (outcome === "inquiry_only") {
        L.push({ speaker: "caller", text: "Hi, what time do you close tonight?", t: "0:07" });
        L.push({ speaker: "agent", text: "We're open until 11 PM tonight. Is there anything else I can help with, perhaps placing an order?", t: "0:11" });
        L.push({ speaker: "caller", text: "No thanks, just checking. Bye!", t: "0:18" });
        return L;
    }
    L.push({ speaker: "caller", text: "Hi, I'd like to place an order for pickup.", t: "0:08" });
    L.push({ speaker: "agent", text: "Of course! What would you like to order?", t: "0:11" });
    items.forEach((it, i) => {
        const mod = it.modifiers && it.modifiers.length ? `, ${it.modifiers.join(" and ")}` : "";
        L.push({ speaker: "caller", text: `${it.qty} ${it.name}${mod}.`, t: `${0}:${pad(20 + i * 12)}` });
        L.push({ speaker: "agent", text: `Got it — ${it.qty} ${it.name}. Anything else?`, t: `${0}:${pad(24 + i * 12)}` });
    });
    if (outcome === "transferred") {
        L.push({ speaker: "caller", text: "Actually, can I add a catering order for 40 people this weekend?", t: "1:40" });
        L.push({ speaker: "agent", text: "That's a great question — let me connect you with a team member who handles catering. One moment.", t: "1:46" });
        return L;
    }
    if (outcome === "abandoned") {
        L.push({ speaker: "caller", text: "Hmm, let me think about it… actually I'll call back later.", t: "1:30" });
        return L;
    }
    // order_completed
    const total = items.reduce((s, it) => s + it.qty * it.price, 0);
    L.push({ speaker: "caller", text: "That's everything.", t: "1:50" });
    L.push({ speaker: "agent", text: `Your total comes to $${total.toFixed(2)}. Can I confirm and send this to the kitchen?`, t: "1:55" });
    L.push({ speaker: "caller", text: "Yes, please.", t: "2:02" });
    L.push({ speaker: "agent", text: "Perfect! Your order is confirmed and will be ready in about 25 minutes. Thank you!", t: "2:06" });
    return L;
}

/* ─── Pool generation ───────────────────────────────────────────────────── */

const DAYS_BACK = 70;
let CALL_POOL: VACall[] | null = null;

function generatePool(): VACall[] {
    const calls: VACall[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    let callSeq = 2000;

    // ~40 distinct repeat-caller hashes to reuse, each with a stable display name
    const FIRST_NAMES = ["James", "Maria", "David", "Sarah", "Mohammed", "Emily", "Carlos", "Aisha", "John", "Priya", "Michael", "Linda", "Wei", "Fatima", "Robert", "Sofia", "Daniel", "Grace", "Ahmed", "Olivia", "Kevin", "Nadia", "Thomas", "Leila"];
    const LAST_INITIALS = ["A.", "B.", "C.", "D.", "K.", "L.", "M.", "P.", "R.", "S.", "T.", "W."];
    const nameFor = (i: number) => `${FIRST_NAMES[i % FIRST_NAMES.length]} ${LAST_INITIALS[(i * 5) % LAST_INITIALS.length]}`;
    const repeatHashes = Array.from({ length: 40 }, (_, i) => `rh_${(i + 1).toString(16)}a${i * 7}`);
    const repeatNames = new Map<string, string>(repeatHashes.map((h, i) => [h, nameFor(i)]));

    for (let dayOffset = DAYS_BACK; dayOffset >= 0; dayOffset--) {
        const d = new Date(today);
        d.setDate(today.getDate() - dayOffset);
        const dateStr = toDateStr(d);
        const dow = d.getDay(); // 0 Sun … 6 Sat
        const seed = parseInt(dateStr.replace(/-/g, ""), 10);
        const rnd = mulberry32(seed);

        // weekend busier; slight upward trend toward present for nicer deltas
        const weekendBoost = dow === 5 || dow === 6 ? 1.35 : dow === 0 ? 1.15 : 1;
        const trend = 1 + (DAYS_BACK - dayOffset) / DAYS_BACK / 6; // ~+17% over the window
        const baseCalls = 32 * weekendBoost * trend;
        const dayCalls = Math.max(6, Math.round(baseCalls + (rnd() - 0.5) * 10));

        for (let c = 0; c < dayCalls; c++) {
            const hour = weightedHour(rnd);
            const minute = Math.floor(rnd() * 60);
            const start = new Date(d);
            start.setHours(hour, minute, 0, 0);

            // outcome distribution
            const roll = rnd();
            let outcome: CallOutcome;
            if (roll < 0.3) outcome = "order_completed";
            else if (roll < 0.45) outcome = "transferred";
            else if (roll < 0.57) outcome = "abandoned";
            else if (roll < 0.85) outcome = "inquiry_only";
            else outcome = "hangup";

            // duration
            let durationSeconds: number;
            if (outcome === "hangup") durationSeconds = Math.floor(rnd() * 12) + 3;
            else if (outcome === "inquiry_only") durationSeconds = Math.floor(rnd() * 60) + 25;
            else if (outcome === "transferred") durationSeconds = Math.floor(rnd() * 180) + 120;
            else if (outcome === "abandoned") durationSeconds = Math.floor(rnd() * 120) + 60;
            else durationSeconds = Math.floor(rnd() * 180) + 150; // order_completed

            // funnel flags
            const orderingIntent = outcome === "order_completed" || outcome === "abandoned" || (outcome === "transferred" && rnd() < 0.5);
            const orderStarted = orderingIntent && (outcome === "order_completed" || outcome === "abandoned" || rnd() < 0.4);
            const orderConfirmed = outcome === "order_completed" || (orderStarted && outcome === "abandoned" && rnd() < 0.2);
            const orderSubmitted = outcome === "order_completed";

            // order items
            let orderItems: OrderItem[] | undefined;
            let orderTotal: number | undefined;
            let orderId: string | undefined;
            let orderStatus: VACall["orderStatus"];
            if (orderSubmitted) {
                const n = Math.floor(rnd() * 3) + 1;
                const chosen: OrderItem[] = [];
                for (let k = 0; k < n; k++) {
                    const m = pick(rnd, MENU);
                    const qty = Math.floor(rnd() * 2) + 1;
                    const mods = rnd() < 0.35 ? [pick(rnd, MODIFIERS)] : undefined;
                    chosen.push({ name: m.name, qty, price: m.price, modifiers: mods });
                }
                orderItems = chosen;
                orderTotal = Math.round(chosen.reduce((s, it) => s + it.qty * it.price, 0) * 100) / 100;
                orderId = `ORD-${callSeq + 100}`;
                const sRoll = rnd();
                orderStatus = sRoll < 0.7 ? "Picked Up" : sRoll < 0.92 ? "Confirmed" : "Cancelled";
            }

            // transfer reason
            let transferReason: TransferReason | undefined;
            if (outcome === "transferred") {
                const tr = rnd();
                transferReason = tr < 0.38 ? "customer_requested" : tr < 0.68 ? "out_of_scope" : tr < 0.89 ? "agent_couldnt_understand" : "repeated_failures";
            }

            // 86'd encounters (~9% of non-hangup calls)
            const eightySixed: EightySixEncounter[] = [];
            if (outcome !== "hangup" && rnd() < 0.09) {
                const itemName = pick(rnd, EIGHTY_SIX_POOL);
                const menuItem = MENU.find((m) => m.name === itemName)!;
                const aRoll = rnd();
                const action: EightySixAction =
                    aRoll < 0.55 ? "accepted_alternative" : aRoll < 0.78 ? "removed_item" : aRoll < 0.9 ? "abandoned" : "transferred";
                eightySixed.push({
                    itemName,
                    price: menuItem.price,
                    customerAction: action,
                    alternativeOffered: ALTERNATIVES[itemName],
                });
            }

            // quality scores (only calls > 10s)
            let qualityOverall: number | undefined;
            let qualityAccuracy: number | undefined;
            let qualityTone: number | undefined;
            let qualityEfficiency: number | undefined;
            if (durationSeconds > 10) {
                const base = 3.6 + rnd() * 1.4; // 3.6–5.0 typical
                const lowCall = rnd() < 0.12; // ~12% problematic
                const center = lowCall ? 2.4 + rnd() * 1.0 : base;
                qualityAccuracy = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
                qualityTone = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
                qualityEfficiency = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
                qualityOverall = +(qualityAccuracy * 0.4 + qualityTone * 0.3 + qualityEfficiency * 0.3).toFixed(1);
            }

            const isRepeatCaller = rnd() < 0.28;
            const callerHash = isRepeatCaller ? pick(rnd, repeatHashes) : `oh_${callSeq.toString(16)}`;
            // Customer name: stable per repeat caller; one-off name otherwise; unknown for blind hangups.
            const customerName = outcome === "hangup" && rnd() < 0.6
                ? "Unknown caller"
                : isRepeatCaller
                    ? (repeatNames.get(callerHash) || "Unknown caller")
                    : `${pick(rnd, FIRST_NAMES)} ${pick(rnd, LAST_INITIALS)}`;

            calls.push({
                callId: `VA-${callSeq++}`,
                startTime: start.toISOString(),
                date: dateStr,
                hour,
                durationSeconds,
                outcome,
                orderingIntent,
                orderStarted,
                orderConfirmed,
                orderSubmitted,
                orderId,
                orderTotal,
                orderItems,
                orderStatus,
                transferReason,
                eightySixed,
                qualityOverall,
                qualityAccuracy,
                qualityTone,
                qualityEfficiency,
                isRepeatCaller,
                callerHash,
                customerName,
                transcript: buildTranscript(outcome, orderItems || [], rnd),
            });
        }
    }
    return calls;
}

export function getCallPool(): VACall[] {
    if (!CALL_POOL) CALL_POOL = generatePool();
    return CALL_POOL;
}

/* ─── Date range resolution ─────────────────────────────────────────────── */

export interface DateRange { startDate: string; endDate: string; days: number; }

export function resolveDateRange(period: string): DateRange {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const mk = (start: Date, end: Date): DateRange => {
        const days = Math.round((end.getTime() - start.getTime()) / 86400000) + 1;
        return { startDate: toDateStr(start), endDate: toDateStr(end), days };
    };

    const norm = (period || "7D").trim();

    // Delegate to the same token → calendar-range resolver every other report
    // uses (utils/reportPeriod.ts, GLOBAL-DP). Handles Today / Yesterday / 7D /
    // 30D / This Month / This Year / Last Month / Last 3 Months / Last 6 Months /
    // Last Year / custom:from:to — this was previously reimplemented locally
    // with only a handful of cases, so an unhandled token like "This Year"
    // silently fell through to the 7D default below instead of the correct range.
    const shared = resolveTokenRange(norm);
    if (shared) {
        const s = new Date(shared.startDate + "T00:00:00");
        const e = new Date(shared.endDate + "T00:00:00");
        return mk(s, e);
    }

    switch (norm) {
        case "7D":
        default: {
            const s = new Date(today);
            s.setDate(today.getDate() - 7);
            return mk(s, yesterday);
        }
    }
}

// Prior period of equal length immediately preceding the given range.
export function priorRange(r: DateRange): DateRange {
    const s = new Date(r.startDate + "T00:00:00");
    const e = new Date(s);
    e.setDate(s.getDate() - 1);
    const ps = new Date(e);
    ps.setDate(e.getDate() - (r.days - 1));
    return { startDate: toDateStr(ps), endDate: toDateStr(e), days: r.days };
}

function callsInRange(pool: VACall[], r: DateRange): VACall[] {
    return pool.filter((c) => c.date >= r.startDate && c.date <= r.endDate);
}

/* ─── Aggregations ──────────────────────────────────────────────────────── */

export interface Aggregate {
    totalCalls: number;
    ordersSubmitted: number;
    conversionRate: number; // %
    revenue: number;
    transfers: number;
    transferRate: number; // %
    eightySixEncounters: number;
    qualityScore: number | null; // avg overall, null if no scored calls
}

function aggregate(calls: VACall[]): Aggregate {
    const totalCalls = calls.length;
    const ordersSubmitted = calls.filter((c) => c.orderSubmitted).length;
    const revenue = calls.reduce((s, c) => s + (c.orderTotal || 0), 0);
    const transfers = calls.filter((c) => c.outcome === "transferred").length;
    const eightySixEncounters = calls.reduce((s, c) => s + c.eightySixed.length, 0);
    const scored = calls.filter((c) => typeof c.qualityOverall === "number");
    const qualityScore = scored.length ? scored.reduce((s, c) => s + (c.qualityOverall || 0), 0) / scored.length : null;
    return {
        totalCalls,
        ordersSubmitted,
        conversionRate: totalCalls ? (ordersSubmitted / totalCalls) * 100 : 0,
        revenue,
        transfers,
        transferRate: totalCalls ? (transfers / totalCalls) * 100 : 0,
        eightySixEncounters,
        qualityScore,
    };
}

/* ─── Full derived model for the report ─────────────────────────────────── */

export interface DayBucket { date: string; label: string; calls: number; orders: number; revenue: number; quality: number | null; }
export interface HourBucket { hour: number; label: string; calls: number; }
export interface TopItem { name: string; orders: number; revenue: number; }
export interface OutcomeBucket { key: CallOutcome; label: string; count: number; }
export interface TransferReasonBucket { key: TransferReason | "other"; label: string; count: number; }
export interface EightySixItem {
    name: string; encounters: number; price: number; revenueImpact: number;
    accepted: number; removed: number; abandoned: number; transferred: number;
}

export interface VoiceAgentData {
    range: DateRange;
    current: Aggregate;
    prior: Aggregate;
    calls: VACall[]; // current period, sorted by time desc
    byDay: DayBucket[];
    byHour: HourBucket[];
    peakHour: HourBucket | null;
    avgCallsPerHour: number;
    topItems: TopItem[]; // by count
    topItemsByRevenue: TopItem[];
    outcomes: OutcomeBucket[];
    // time saved
    callsWithoutTransfer: number;
    avgDurationSeconds: number;
    timeSavedMinutes: number;
    laborCost: number;
    // funnel
    funnel: { totalCalls: number; intent: number; started: number; confirmed: number; submitted: number };
    // transfers
    transferReasons: TransferReasonBucket[];
    // 86'd
    eightySixItems: EightySixItem[];
    substitutionSuccessRate: number;
    eightySixRevenueImpact: number;
    // quality
    quality: { overall: number | null; accuracy: number | null; tone: number | null; efficiency: number | null };
    qualityTrend: { date: string; label: string; overall: number | null; accuracy: number | null; tone: number | null; efficiency: number | null }[];
}

const OUTCOME_LABELS: Record<CallOutcome, string> = {
    order_completed: "Order Placed",
    transferred: "Transferred",
    abandoned: "Abandoned",
    inquiry_only: "Inquiry",
    hangup: "Hangup",
};

const TRANSFER_LABELS: Record<TransferReason | "other", string> = {
    customer_requested: "Customer Requested",
    agent_couldnt_understand: "Agent Couldn't Understand",
    out_of_scope: "Out-of-Scope Request",
    repeated_failures: "Repeated Failures",
    other: "Other",
};

export const dayLabel = (dateStr: string) => {
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export const hourLabel = (h: number) => {
    const ampm = h < 12 ? "AM" : "PM";
    const hr = h % 12 === 0 ? 12 : h % 12;
    return `${hr} ${ampm}`;
};

export function deriveVoiceAgentData(period: string): VoiceAgentData {
    const pool = getCallPool();
    const range = resolveDateRange(period);
    const prior = priorRange(range);
    const cur = callsInRange(pool, range);
    const pri = callsInRange(pool, prior);

    const current = aggregate(cur);
    const priorAgg = aggregate(pri);

    // by day
    const dayMap = new Map<string, DayBucket>();
    for (const c of cur) {
        let b = dayMap.get(c.date);
        if (!b) { b = { date: c.date, label: dayLabel(c.date), calls: 0, orders: 0, revenue: 0, quality: null }; dayMap.set(c.date, b); }
        b.calls++;
        if (c.orderSubmitted) b.orders++;
        b.revenue += c.orderTotal || 0;
    }
    // day quality avg
    for (const [date, b] of dayMap) {
        const scored = cur.filter((c) => c.date === date && typeof c.qualityOverall === "number");
        b.quality = scored.length ? +(scored.reduce((s, c) => s + (c.qualityOverall || 0), 0) / scored.length).toFixed(2) : null;
    }
    const byDay = Array.from(dayMap.values()).sort((a, b) => a.date.localeCompare(b.date));

    // by hour (average per day across range)
    const hourTotals = new Map<number, number>();
    for (const c of cur) hourTotals.set(c.hour, (hourTotals.get(c.hour) || 0) + 1);
    const numDays = byDay.length || 1;
    const byHour: HourBucket[] = OPERATING_HOURS
        .map((h) => ({ hour: h, label: hourLabel(h), calls: Math.round(((hourTotals.get(h) || 0) / numDays) * 10) / 10 }))
        .filter((b) => b.calls > 0);
    const peakHour = byHour.length ? byHour.reduce((a, b) => (b.calls > a.calls ? b : a)) : null;
    const avgCallsPerHour = byHour.length ? +(byHour.reduce((s, b) => s + b.calls, 0) / byHour.length).toFixed(1) : 0;

    // top items
    const itemMap = new Map<string, TopItem>();
    for (const c of cur) {
        if (!c.orderItems) continue;
        for (const it of c.orderItems) {
            let t = itemMap.get(it.name);
            if (!t) { t = { name: it.name, orders: 0, revenue: 0 }; itemMap.set(it.name, t); }
            t.orders += it.qty;
            t.revenue += it.qty * it.price;
        }
    }
    const allItems = Array.from(itemMap.values());
    const topItems = [...allItems].sort((a, b) => b.orders - a.orders || a.name.localeCompare(b.name)).slice(0, 10);
    const topItemsByRevenue = [...allItems].sort((a, b) => b.revenue - a.revenue || a.name.localeCompare(b.name)).slice(0, 10);

    // outcomes
    const outcomeOrder: CallOutcome[] = ["order_completed", "transferred", "abandoned", "inquiry_only", "hangup"];
    const outcomes: OutcomeBucket[] = outcomeOrder
        .map((k) => ({ key: k, label: OUTCOME_LABELS[k], count: cur.filter((c) => c.outcome === k).length }))
        .sort((a, b) => b.count - a.count);

    // time saved
    const callsWithoutTransfer = cur.filter((c) => c.outcome !== "transferred").length;
    const nonTrivial = cur.filter((c) => c.durationSeconds > 10);
    const avgDurationSeconds = nonTrivial.length ? nonTrivial.reduce((s, c) => s + c.durationSeconds, 0) / nonTrivial.length : 0;
    const timeSavedMinutes = (callsWithoutTransfer * avgDurationSeconds) / 60;
    const laborCost = (timeSavedMinutes / 60) * 15;

    // funnel
    const funnel = {
        totalCalls: current.totalCalls,
        intent: cur.filter((c) => c.orderingIntent).length,
        started: cur.filter((c) => c.orderStarted).length,
        confirmed: cur.filter((c) => c.orderConfirmed).length,
        submitted: current.ordersSubmitted,
    };

    // transfer reasons
    const trMap = new Map<TransferReason | "other", number>();
    for (const c of cur) {
        if (c.outcome !== "transferred") continue;
        const key = c.transferReason || "other";
        trMap.set(key, (trMap.get(key) || 0) + 1);
    }
    const transferReasons: TransferReasonBucket[] = (Array.from(trMap.keys()) as (TransferReason | "other")[])
        .map((k) => ({ key: k, label: TRANSFER_LABELS[k], count: trMap.get(k) || 0 }))
        .sort((a, b) => (a.key === "other" ? 1 : b.key === "other" ? -1 : b.count - a.count));

    // 86'd items
    const esMap = new Map<string, EightySixItem>();
    for (const c of cur) {
        for (const e of c.eightySixed) {
            let it = esMap.get(e.itemName);
            if (!it) { it = { name: e.itemName, encounters: 0, price: e.price, revenueImpact: 0, accepted: 0, removed: 0, abandoned: 0, transferred: 0 }; esMap.set(e.itemName, it); }
            it.encounters++;
            it.revenueImpact += e.price;
            if (e.customerAction === "accepted_alternative") it.accepted++;
            else if (e.customerAction === "removed_item") it.removed++;
            else if (e.customerAction === "abandoned") it.abandoned++;
            else it.transferred++;
        }
    }
    const eightySixItems = Array.from(esMap.values()).sort((a, b) => b.encounters - a.encounters);
    const totalEs = current.eightySixEncounters;
    const totalAccepted = eightySixItems.reduce((s, i) => s + i.accepted, 0);
    const substitutionSuccessRate = totalEs ? (totalAccepted / totalEs) * 100 : 0;
    const eightySixRevenueImpact = eightySixItems.reduce((s, i) => s + i.revenueImpact, 0);

    // quality dimensions
    const scored = cur.filter((c) => typeof c.qualityOverall === "number");
    const avgDim = (sel: (c: VACall) => number | undefined) =>
        scored.length ? +(scored.reduce((s, c) => s + (sel(c) || 0), 0) / scored.length).toFixed(1) : null;
    const quality = {
        overall: current.qualityScore != null ? +current.qualityScore.toFixed(1) : null,
        accuracy: avgDim((c) => c.qualityAccuracy),
        tone: avgDim((c) => c.qualityTone),
        efficiency: avgDim((c) => c.qualityEfficiency),
    };
    const qualityTrend = byDay.map((b) => {
        const dayCalls = cur.filter((c) => c.date === b.date && typeof c.qualityOverall === "number");
        const avg = (sel: (c: VACall) => number | undefined) =>
            dayCalls.length ? +(dayCalls.reduce((s, c) => s + (sel(c) || 0), 0) / dayCalls.length).toFixed(2) : null;
        return {
            date: b.date,
            label: b.label,
            overall: avg((c) => c.qualityOverall),
            accuracy: avg((c) => c.qualityAccuracy),
            tone: avg((c) => c.qualityTone),
            efficiency: avg((c) => c.qualityEfficiency),
        };
    });

    const calls = [...cur].sort((a, b) => b.startTime.localeCompare(a.startTime));

    return {
        range, current, prior: priorAgg, calls,
        byDay, byHour, peakHour, avgCallsPerHour,
        topItems, topItemsByRevenue, outcomes,
        callsWithoutTransfer, avgDurationSeconds, timeSavedMinutes, laborCost,
        funnel, transferReasons, eightySixItems, substitutionSuccessRate, eightySixRevenueImpact,
        quality, qualityTrend,
    };
}

/* ─── Formatting helpers ────────────────────────────────────────────────── */

export const fmtInt = (n: number) => Math.round(n).toLocaleString("en-US");
export const fmtCompact = (n: number) => (n > 9999 ? `${(n / 1000).toFixed(1)}k` : fmtInt(n));
// Currency symbol resolved live from the outlet's country (IN → ₹, US → $, else nothing)
// — never hardcode '$'. See .claude/skills/reports-currency-by-country.
export const fmtMoney = (n: number) => `${getCurrencySymbol()}${Math.round(n).toLocaleString("en-US")}`;
export const fmtMoneyCompact = (n: number) => (n >= 10000 ? `${getCurrencySymbol()}${(n / 1000).toFixed(1)}k` : `${getCurrencySymbol()}${n.toFixed(2)}`);
export const fmtMoney2 = (n: number) => `${getCurrencySymbol()}${n.toFixed(2)}`;
export const fmtPct = (n: number) => `${n.toFixed(1)}%`;

export function fmtDuration(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds % 60);
    return `${m}m ${s}s`;
}

export function fmtHrsMin(minutes: number): string {
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return `${h} hrs ${m} min`;
}

export function fmtCallTime(iso: string, sameDay: boolean): string {
    const d = new Date(iso);
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    if (sameDay) return time;
    return `${d.toLocaleDateString("en-US", { month: "short", day: "numeric" })} ${time}`;
}

/* ─── Mock quality score generator (keyed by any stable string, e.g. callSid) ──
   Used to keep showing a quality-score row on the Call Transcript slide-over
   for API-backed calls, since the real transcript endpoint doesn't return
   quality scores yet. Deterministic per key so reopening the same call shows
   the same numbers. ─────────────────────────────────────────────────────── */
export interface MockQuality { overall: number; accuracy: number; tone: number; efficiency: number }

export function mockQualityForKey(key: string): MockQuality {
    let h = 0;
    for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
    const rnd = mulberry32(h >>> 0);
    const base = 3.6 + rnd() * 1.4;
    const lowCall = rnd() < 0.12;
    const center = lowCall ? 2.4 + rnd() * 1.0 : base;
    const accuracy = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
    const tone = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
    const efficiency = Math.min(5, Math.max(1, +(center + (rnd() - 0.5) * 0.6).toFixed(1)));
    const overall = +(accuracy * 0.4 + tone * 0.3 + efficiency * 0.3).toFixed(1);
    return { overall, accuracy, tone, efficiency };
}

/* Delta helpers ----------------------------------------------------------- */

export interface Delta { text: string; tone: "up" | "down" | "neutral"; positive: boolean; }

// Relative % change delta (counts, revenue). invert=true → decrease is good.
export function deltaPct(cur: number, prev: number, invert = false): Delta {
    if (!prev) return { text: "N/A", tone: "neutral", positive: false };
    const change = ((cur - prev) / prev) * 100;
    const up = change >= 0;
    const positive = invert ? !up : up;
    return { text: `${up ? "↑" : "↓"} ${Math.abs(change).toFixed(1)}%`, tone: up ? "up" : "down", positive };
}

// Percentage-point delta (rates). invert=true → decrease is good.
export function deltaPP(cur: number, prev: number, invert = false): Delta {
    const change = cur - prev;
    const up = change >= 0;
    const positive = invert ? !up : up;
    return { text: `${up ? "↑" : "↓"} ${Math.abs(change).toFixed(1)}pp`, tone: up ? "up" : "down", positive };
}

// Absolute point delta (quality score).
export function deltaAbs(cur: number | null, prev: number | null): Delta {
    if (cur == null || prev == null) return { text: "N/A", tone: "neutral", positive: false };
    const change = cur - prev;
    const up = change >= 0;
    return { text: `${up ? "↑" : "↓"} ${Math.abs(change).toFixed(1)}`, tone: up ? "up" : "down", positive: up };
}
